import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import fixWebmDuration from 'fix-webm-duration';
import CameraControles from './CameraControles';

// Grava video 720p direto no app (getUserMedia + MediaRecorder), em vez de usar
// o gravador nativo do celular (que sai em 1080p e estoura o limite de tamanho).
// Recording ao vivo NAO tem o bug de corte do re-encode: o que voce filma e o
// que sai. ~2,5 Mbps => 10s ≈ 3 MB, 15s ≈ 4,5 MB (cabe no limite de 8 MB).

export function suportaGravador(): boolean {
  return Boolean(navigator.mediaDevices?.getUserMedia) && typeof MediaRecorder !== 'undefined';
}

function escolherMime(): string {
  const ops = ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4'];
  return ops.find((m) => {
    try { return MediaRecorder.isTypeSupported(m); } catch { return false; }
  }) || '';
}

function extDoMime(mime: string): string {
  return mime.includes('mp4') ? 'mp4' : 'webm';
}

export default function GravadorVideo({
  maxSegundos,
  onGravado,
  onFechar,
}: {
  maxSegundos: number;
  onGravado: (file: File) => void;
  onFechar: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const inicioRef = useRef<number>(0);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [gravando, setGravando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [previewUrl, setPreviewUrl] = useState('');
  const arquivoRef = useRef<File | null>(null);

  function pararTimer() {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  function pararStream() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStream(null);
  }

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const media = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
            // Trava 30 fps: sem isto, muitos celulares abrem a camera em 60 fps
            // e o webm sai marcado como 30 fps -> reproducao em camera lenta (2x).
            frameRate: { ideal: 30, max: 30 },
          },
        });
        if (cancelado) { media.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = media;
        setStream(media);
        if (videoRef.current) {
          videoRef.current.srcObject = media;
          videoRef.current.muted = true;
        }
      } catch {
        if (!cancelado) setErro('Não foi possível acessar a câmera/microfone. Verifique a permissão do navegador.');
      } finally {
        if (!cancelado) setCarregando(false);
      }
    })();
    return () => {
      cancelado = true;
      pararTimer();
      pararStream();
      if (arquivoRef.current) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function iniciar() {
    const stream = streamRef.current;
    if (!stream) return;
    const mime = escolherMime();
    if (!mime) { setErro('Este navegador não suporta gravação de vídeo. Use "Selecionar vídeo".'); return; }

    chunksRef.current = [];
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 2_500_000, audioBitsPerSecond: 128_000 });
    } catch {
      setErro('Falha ao iniciar a gravação neste navegador. Use "Selecionar vídeo".');
      return;
    }
    recorderRef.current = recorder;
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    recorder.onstop = async () => {
      pararTimer();
      const tipo = mime.split(';')[0];
      let blob = new Blob(chunksRef.current, { type: tipo });
      if (!blob.size) { setErro('A gravação saiu vazia. Tente novamente.'); setGravando(false); return; }
      // O webm do MediaRecorder sai SEM duracao no cabeçalho -> preview preto,
      // scrub quebrado. Injeta a duracao real (medida com performance.now).
      const durMs = Math.max(0, Math.round(performance.now() - inicioRef.current));
      if (tipo.includes('webm') && durMs > 0) {
        try { blob = await fixWebmDuration(blob, durMs, { logger: false }); } catch { /* mantém o original */ }
      }
      const file = new File([blob], `video-os.${extDoMime(tipo)}`, { type: tipo });
      arquivoRef.current = file;
      setPreviewUrl(URL.createObjectURL(blob));
      setGravando(false);
    };

    inicioRef.current = performance.now();
    recorder.start(500);
    setSegundos(0);
    setGravando(true);
    setErro('');
    timerRef.current = window.setInterval(() => {
      setSegundos((s) => {
        const prox = s + 1;
        if (prox >= maxSegundos) parar();
        return prox;
      });
    }, 1000);
  }

  function parar() {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    pararTimer();
  }

  function regravar() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    arquivoRef.current = null;
    setPreviewUrl('');
    setSegundos(0);
    setErro('');
  }

  function usar() {
    const file = arquivoRef.current;
    if (!file) return;
    pararStream();
    onGravado(file);
  }

  function fechar() {
    parar();
    pararStream();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    onFechar();
  }

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 3000, padding: 16,
        background: 'rgba(15, 23, 42, 0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div style={{ width: '100%', maxWidth: 560, borderRadius: 16, background: '#fff', overflow: 'hidden', boxShadow: '0 30px 80px rgba(15,23,42,0.35)' }}>
        <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--borda, #e2e8f0)' }}>
          <strong>Gravar vídeo (720p)</strong>
          <button type="button" className="btn sec sm" onClick={fechar}>Fechar</button>
        </div>

        <div style={{ background: '#020617', minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          {carregando && <span style={{ color: '#fff' }}>Abrindo câmera...</span>}
          {!carregando && erro && <p style={{ color: '#fff', padding: 18, textAlign: 'center', margin: 0 }}>{erro}</p>}

          {!erro && !previewUrl && (
            <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', maxHeight: '70vh', display: carregando ? 'none' : 'block', objectFit: 'contain' }} />
          )}
          {!erro && !previewUrl && !carregando && <CameraControles stream={stream} />}
          {previewUrl && (
            <video src={previewUrl} controls playsInline style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
          )}

          {gravando && (
            <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', alignItems: 'center', gap: 7, background: 'rgba(220,38,38,0.92)', color: '#fff', padding: '5px 10px', borderRadius: 999, fontWeight: 800, fontSize: 13 }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#fff' }} />
              REC {segundos}s / {maxSegundos}s
            </div>
          )}
        </div>

        <div style={{ padding: 14, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {!erro && !previewUrl && !gravando && (
            <button type="button" className="btn" disabled={carregando} onClick={iniciar}>● Gravar</button>
          )}
          {gravando && (
            <button type="button" className="btn" style={{ background: 'var(--erro, #dc2626)', boxShadow: 'none' }} onClick={parar}>■ Parar ({maxSegundos - segundos}s)</button>
          )}
          {previewUrl && (
            <>
              <button type="button" className="btn sec sm" onClick={regravar}>Regravar</button>
              <button type="button" className="btn" onClick={usar}>Usar este vídeo</button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
